import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { PaginationWrapper } from '../ui/PaginationWrapper';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Shield, 
  Search, 
  Edit, 
  Eye,
  UserCog,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import RoleHierarchyDiagram from './RoleHierarchyDiagram';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'user' | 'marketing' | 'marketing_manager' | 'admin';
  managedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  isBlocked: boolean;
  createdAt: string;
}

const rolePermissions = {
  admin: {
    label: 'Administrator',
    color: 'bg-red-500',
    permissions: [
      'Full system access',
      'Manage all users (including admins, marketing managers, marketing users)',
      'Create and manage marketing managers',
      'Assign leads to any marketing user or manager',
      'View all analytics and reports',
      'Manage subscription plans',
      'Manage payments and earnings',
      'Configure system settings',
      'Manage blog posts and community',
      'Change user roles',
      'Access all admin panels'
    ]
  },
  marketing_manager: {
    label: 'Marketing Manager',
    color: 'bg-purple-500',
    permissions: [
      'Manage marketing users under their supervision',
      'Assign leads to their marketing users',
      'View performance of their team',
      'View analytics for their team',
      'Cannot access admin panel',
      'Cannot change user roles',
      'Cannot manage subscription plans',
      'Limited to their assigned team only'
    ]
  },
  marketing: {
    label: 'Marketing User',
    color: 'bg-blue-500',
    permissions: [
      'View assigned leads',
      'Contact assigned users',
      'Update lead status',
      'View their own performance metrics',
      'Cannot assign leads to others',
      'Cannot access admin panel',
      'Cannot manage other users',
      'Must be assigned by admin or marketing manager'
    ]
  },
  user: {
    label: 'Regular User',
    color: 'bg-gray-500',
    permissions: [
      'Access matrimony platform features',
      'Create and edit their profile',
      'Search and match with other users',
      'Send and receive messages (with subscription)',
      'Purchase subscription plans',
      'No administrative access',
      'No lead management access'
    ]
  }
};

export function AdminRoleManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newRole, setNewRole] = useState<string>('');
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [managers, setManagers] = useState<User[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage, searchQuery, filterRole]);

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const { data } = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchQuery || undefined,
          role: filterRole !== 'all' ? filterRole : undefined
        }
      });
      
      const responseData = data.data || data;
      const usersData = responseData.users || [];
      const pagination = responseData.pagination || {};
      
      setUsers(usersData);
      setFilteredUsers(usersData);
      setTotalUsers(pagination.total || usersData.length);
      setTotalPages(pagination.totalPages || Math.ceil((pagination.total || usersData.length) / itemsPerPage));
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/admin/roles/managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setManagers(data.managers || []);
    } catch (error: any) {
      console.error('Error fetching managers:', error);
    }
  };

  // Backend now handles filtering, so this function is no longer needed
  // Keeping it as a no-op for compatibility
  const filterUsers = () => {
    // Backend handles filtering via query params
  };
  
  // Backend handles pagination - just use the filtered users directly
  const paginatedUsers = filteredUsers;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterRole]);

  // Ensure currentPage doesn't exceed totalPages when data changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleEditRole = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setSelectedManager(user.managedBy?._id || 'none');
    setShowEditDialog(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) {
      toast.error('Please select a role');
      return;
    }

    // Validate manager selection for marketing users
    if (newRole === 'marketing' && (!selectedManager || selectedManager === 'none')) {
      toast.error('Please select a manager for marketing users');
      return;
    }

    try {
      setUpdating(true);
      const token = localStorage.getItem('token');
      
      const payload: any = {
        userId: selectedUser._id,
        role: newRole
      };

      if (newRole === 'marketing' || newRole === 'marketing_manager') {
        payload.managedBy = (selectedManager && selectedManager !== 'none') ? selectedManager : null;
      }

      await axios.put(
        `${API_URL}/admin/roles/update-role`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('User role updated successfully');
      setShowEditDialog(false);
      fetchUsers();
      fetchManagers();
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.message || 'Failed to update role');
    } finally {
      setUpdating(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleData = rolePermissions[role as keyof typeof rolePermissions];
    return (
      <Badge className={`${roleData.color} text-white`}>
        {roleData.label}
      </Badge>
    );
  };

  const stats = {
    totalUsers: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    marketingManagers: users.filter(u => u.role === 'marketing_manager').length,
    marketingUsers: users.filter(u => u.role === 'marketing').length,
    regularUsers: users.filter(u => u.role === 'user').length
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Role Management</h2>
          <p className="text-gray-600 text-sm mt-1">
            Manage user roles and permissions across the platform
          </p>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users">User Roles</TabsTrigger>
          <TabsTrigger value="permissions">Role Permissions</TabsTrigger>
          <TabsTrigger value="hierarchy">Hierarchy Guide</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Users</p>
                    <p className="text-2xl font-bold">{stats.totalUsers}</p>
                  </div>
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Admins</p>
                    <p className="text-2xl font-bold">{stats.admins}</p>
                  </div>
                  <Shield className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Marketing Managers</p>
                    <p className="text-2xl font-bold">{stats.marketingManagers}</p>
                  </div>
                  <UserCog className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Marketing Users</p>
                    <p className="text-2xl font-bold">{stats.marketingUsers}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Regular Users</p>
                    <p className="text-2xl font-bold">{stats.regularUsers}</p>
                  </div>
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, email, or phone..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                    <SelectItem value="marketing">Marketing User</SelectItem>
                    <SelectItem value="user">Regular User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle>Users ({filteredUsers.length})</CardTitle>
              <CardDescription>
                Click on a user to change their role
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No users found
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Name</th>
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-left py-3 px-4">Phone</th>
                        <th className="text-left py-3 px-4">Role</th>
                        <th className="text-left py-3 px-4">Managed By</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map((user) => (
                        <tr key={user._id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            {user.firstName} {user.lastName}
                          </td>
                          <td className="py-3 px-4">{user.email}</td>
                          <td className="py-3 px-4">{user.phone}</td>
                          <td className="py-3 px-4">
                            {getRoleBadge(user.role)}
                          </td>
                          <td className="py-3 px-4">
                            {user.managedBy ? (
                              <span className="text-sm text-gray-600">
                                {user.managedBy.firstName} {user.managedBy.lastName}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {user.isBlocked ? (
                              <Badge variant="destructive">Blocked</Badge>
                            ) : (
                              <Badge variant="default" className="bg-green-500">Active</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditRole(user)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Change Role
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination */}
              {filteredUsers.length > 0 && !loading && (
                <div className="mt-6">
                  <PaginationWrapper
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalUsers}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                    showItemsPerPage={true}
                    itemsPerPageOptions={[10, 20, 50, 100]}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="permissions" className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Role Hierarchy</p>
                <p>Admin → Marketing Manager → Marketing User → Regular User</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(rolePermissions).map(([role, data]) => (
              <Card key={role}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${data.color}`}></div>
                      {data.label}
                    </CardTitle>
                    <Badge className={data.color}>{role.toUpperCase()}</Badge>
                  </div>
                  <CardDescription>
                    Permissions and access levels
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {data.permissions.map((permission, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        {permission.startsWith('Cannot') || permission.startsWith('Must') || permission.startsWith('Limited') ? (
                          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        )}
                        <span className={permission.startsWith('Cannot') || permission.startsWith('Must') || permission.startsWith('Limited') ? 'text-gray-600' : ''}>
                          {permission}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="hierarchy" className="space-y-6">
          <RoleHierarchyDiagram />
        </TabsContent>
      </Tabs>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.firstName} {selectedUser?.lastName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Current Role</label>
              <div className="p-3 bg-gray-50 rounded-lg">
                {selectedUser && getRoleBadge(selectedUser.role)}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">New Role</label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Regular User</SelectItem>
                  <SelectItem value="marketing">Marketing User</SelectItem>
                  <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(newRole === 'marketing' || newRole === 'marketing_manager') && (
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Assign to Manager {newRole === 'marketing' && <span className="text-red-500">*</span>}
                </label>
                <Select value={selectedManager} onValueChange={setSelectedManager}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select manager (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {managers.map((manager) => (
                      <SelectItem key={manager._id} value={manager._id}>
                        {manager.firstName} {manager.lastName} ({manager.role === 'admin' ? 'Admin' : 'Marketing Manager'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newRole === 'marketing' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Marketing users must be assigned to a manager
                  </p>
                )}
              </div>
            )}

            {newRole && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-yellow-800">
                    <p className="font-medium mb-1">Role Change Warning</p>
                    <p>Changing this user's role will immediately update their access permissions.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateRole}
              disabled={updating || !newRole}
            >
              {updating ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}